// warehouse/snapshots.js — las dos "últimas fotos conocidas" de sync_meta
// (F6.4b): el user mínimo del boot offline y el {planInfo, usage} del
// Dashboard. Son JSON en sync_meta, no tablas: no son material de estudio,
// son el contexto de sesión/plan que las pantallas necesitan para pintar
// honesto sin red.
//
// user_snapshot — de aquí sale el objeto `user` cuando el boot nativo entra
// en modo offline (AuthContext §4): el /auth/me completo de la última sesión
// autenticada. Se escribe en cada boot/login online exitoso (AuthContext,
// effect sobre user) y SOLO si el dueño registrado de la bodega coincide —
// nunca se escribe el snapshot de un usuario sobre la bodega de otro.
//
// plan_snapshot — decisión firmada del sprint (opción A): write-through cada
// vez que el fetch vivo del Dashboard resuelve; en modo offline el widget de
// plan pinta estos valores. Sin snapshot todavía (bodega nueva) → null y los
// widgets muestran su estado vacío, sin inventar ceros.
//
// Todo best-effort y nativo-only: cualquier fallo degrada a null/no-op,
// jamás rompe un flujo de auth o de pantalla (patrón tokenStore).

import { isNativePlatform, openWarehouse } from './connection';
import { SYNC_META_KEYS } from './schema';

async function readMeta(api, key) {
  const rows = await api.query('SELECT value FROM sync_meta WHERE key = ?;', [key]);
  return rows.length > 0 && rows[0].value ? rows[0].value : null;
}

async function writeMeta(api, key, value) {
  await api.run(
    'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
    [key, value],
    false
  );
}

/**
 * Persiste el user de la sesión autenticada vigente como snapshot de boot.
 * No-op si: web, bodega ilegible, o el dueño registrado NO es este usuario
 * (la bodega de otro jamás recibe un snapshot ajeno).
 */
export function writeUserSnapshot(user) {
  if (!isNativePlatform() || !user?.id) return Promise.resolve();
  return (async () => {
    try {
      const api = await openWarehouse();
      const owner = await readMeta(api, SYNC_META_KEYS.OWNER_USER_ID);
      if (owner !== user.id) return;
      await writeMeta(api, SYNC_META_KEYS.USER_SNAPSHOT, JSON.stringify(user));
    } catch (e) {
      // best-effort: sin snapshot solo significa "sin boot offline esta vez"
    }
  })();
}

/**
 * El user del boot offline, o null si la bodega no puede sostener una
 * entrada: sin bodega legible, sin dueño, sin snapshot, o snapshot de un
 * dueño distinto (jamás se muestran datos ajenos).
 */
export function readOfflineBootUser() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      const owner = await readMeta(api, SYNC_META_KEYS.OWNER_USER_ID);
      const raw = await readMeta(api, SYNC_META_KEYS.USER_SNAPSHOT);
      if (!owner || !raw) return null;
      const user = JSON.parse(raw);
      if (!user || user.id !== owner) return null;
      return user;
    } catch (e) {
      return null;
    }
  })();
}

/** Write-through de la última foto {planInfo, usage} (fetch vivo exitoso). */
export function writePlanSnapshot(snapshot) {
  if (!isNativePlatform() || !snapshot) return Promise.resolve();
  return (async () => {
    try {
      const api = await openWarehouse();
      await writeMeta(api, SYNC_META_KEYS.PLAN_SNAPSHOT, JSON.stringify(snapshot));
    } catch (e) {
      // best-effort
    }
  })();
}

/** La última foto {planInfo, usage} conocida, o null (bodega nueva). */
export function readPlanSnapshot() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      const raw = await readMeta(api, SYNC_META_KEYS.PLAN_SNAPSHOT);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  })();
}

// ── F6.7b — la foto local de la lista de documentos ─────────────────────────
//
// Solo estos campos: los que la tarjeta del Panel pinta. Ni el texto de los
// docs, ni las preguntas, ni nada que ya viva en las tablas de la bodega — la
// foto es LIGERA a propósito (un JSON en sync_meta, no una tabla).
const DOC_SNAPSHOT_FIELDS = [
  'id', 'filename', 'file_type', 'status', 'question_count', 'flashcard_count',
  'image_count', 'error_message', 'created_at', 'processing_started_at',
  'parent_doc_id', 'chunk_index', 'detected_language',
];

function slimDoc(doc) {
  const out = {};
  for (const k of DOC_SNAPSHOT_FIELDS) {
    if (doc[k] !== undefined) out[k] = doc[k];
  }
  return out;
}

/**
 * Write-through de la lista de documentos leída CON SEÑAL. Se llama desde el
 * único punto que ya escribe las otras fotos del Panel (Dashboard, effect de
 * write-through) y solo con dato VIVO: una lista que salió de la propia foto
 * jamás se re-escribe sobre sí misma.
 *
 * No-op si: web, bodega ilegible, o `docs` no es un array. Nunca lanza.
 */
export function writeDocsSnapshot(docs) {
  if (!isNativePlatform() || !Array.isArray(docs)) return Promise.resolve();
  return (async () => {
    try {
      const api = await openWarehouse();
      const slim = docs.filter((d) => d && d.id != null).map(slimDoc);
      await writeMeta(api, SYNC_META_KEYS.DOCS_SNAPSHOT, JSON.stringify(slim));
    } catch (e) {
      // best-effort: sin foto solo significa que este arranque sin red cae al
      // camino de F6.4b (pintar los docs de la bodega), que sigue siendo honesto.
    }
  })();
}

/** La última lista de documentos conocida, o null (nunca se leyó con señal). */
export function readDocsSnapshot() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      const raw = await readMeta(api, SYNC_META_KEYS.DOCS_SNAPSHOT);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  })();
}

/**
 * El TIER local para el candado D-G — {plan, is_guest} del user_snapshot que
 * F6.4b ya escribe en cada login y en cada refresh de perfil (AuthContext,
 * effect sobre `user`). CERO storage nuevo: el plan siempre viajó dentro de
 * ese mismo objeto.
 *
 * Devuelve null ante CUALQUIER duda (sin bodega, sin snapshot, dueño distinto,
 * JSON roto). El candado lee ese null como "deja pasar" — regla dura de D-G.
 */
export function readTierSnapshot() {
  if (!isNativePlatform()) return Promise.resolve(null);
  return (async () => {
    try {
      const api = await openWarehouse();
      const owner = await readMeta(api, SYNC_META_KEYS.OWNER_USER_ID);
      const raw = await readMeta(api, SYNC_META_KEYS.USER_SNAPSHOT);
      if (!owner || !raw) return null;
      const user = JSON.parse(raw);
      if (!user || user.id !== owner) return null;
      return { plan: user.plan ?? null, is_guest: user.is_guest === true };
    } catch (e) {
      return null;
    }
  })();
}
